import { brand } from '../../../shared/themes';
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useMemo, useRef, useState } from 'react';
import {  Button,   Dialog,   DialogActions,  DialogContent,  Grid, Box, Typography, IconButton, Divider, Chip, Stack, alpha, Alert} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import {  AutoCompleteProdutoIdProduto,  BasicSelect,  IVFormErrors,  VFieldMoneyNumber2,  VFieldMoneyNumber4dn,  VForm,  VSwitchNumber,  VTextField, useVForm } from '../../../shared/forms';
import { AutoCompleteCfop } from '../../../shared/forms/AutoCompleteCfop';
import * as yup from 'yup';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import InventoryIcon from '@mui/icons-material/Inventory';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { getAuth } from '../../../GlobalDecode';
import { IProblemaNfe } from '../../../shared/utils/validarNotaFiscal';

interface IFormData {
  iditens: string;
  idprodutos: string;
  vfrete: number;
  voutros: number;
  vicmsst: number;
  ean: string;
  descricao: string;
  ncm: string;
  origem: number;
  cfop: number | null;
  un: string;
  untrib: string;
  qtd: number;
  qtdtrib: number;
  preco: number;
  precotrib: number;
  cst: string;
  vbc: number;
  picms: number;
  vicms: number;
  predbc: number;
  modbc: string;
  modbcst?: string;
  desconto: number;
  vseg: number;
  cstpis: string;
  picmsst: number;
  ppis: number;
  vipi: number;
  pipi: number;
  cstipi: string;
  vbcipi: number;
  vpis: number;
  vbcpis: number;
  cstcofins: string;
  vbccofins: number;
  pcofins: number;
  vcofins: number;
  bcicmsst: number;
  total: number;
  indtot: number;
  filtro: string;
  // Quantidade de GLP derivado de petróleo do produto (só produto ANP
  // cProdANP=210203001) — não é salvo no item, só usado localmente pra
  // decidir a precisão do Preço Tributado (ver `qtdglp` state mais abaixo).
  qtdglp?: number;
}

export interface ConfirmationDialogRawProps {
  id: string;
  value: string;
  cfop: number;
  open: boolean;
  status: number;
  dados?: IFormData;
  onDados: (dados: IFormData) => void;
  onClose: (value?: string) => void;
  // Preenchido quando o item foi aberto a partir da dialog "Verificar Nota"
  // (ver DetalheDaNfe.tsx/handleAbrirItemComInconsistencia) — marca no header
  // o(s) problema(s) que a verificação encontrou nesse item específico.
  problemasParaCorrigir?: IProblemaNfe[];
}

// Mesma regra e descrições do cadastro de produtos (ver DetalheDeProduto.tsx)
// — crt 1 (Simples Nacional) usa CSOSN, qualquer outro usa CST normal.
const OPCOES_CSOSN = [
  { nameBD: '101', descricao: '101 - Tributada c/ permissão de crédito' },
  { nameBD: '102', descricao: '102 - Tributada s/ permissão de crédito' },
  { nameBD: '103', descricao: '103 - Isenção (faixa de receita bruta)' },
  { nameBD: '201', descricao: '201 - Tributada c/ crédito e ICMS ST' },
  { nameBD: '202', descricao: '202 - Tributada s/ crédito e ICMS ST' },
  { nameBD: '203', descricao: '203 - Isenção (faixa de receita) e ICMS ST' },
  { nameBD: '300', descricao: '300 - Imune' },
  { nameBD: '400', descricao: '400 - Não tributada' },
  { nameBD: '500', descricao: '500 - ICMS cobrado antes por ST/antecipação' },
  { nameBD: '900', descricao: '900 - Outros' },
];
const OPCOES_CST = [
  { nameBD: '00', descricao: '00 - Tributada integralmente' },
  { nameBD: '10', descricao: '10 - Tributada e com cobrança do ICMS por substituição tributária' },
  { nameBD: '20', descricao: '20 - Com redução de base de cálculo' },
  { nameBD: '30', descricao: '30 - Isenta ou não tributada e com cobrança do ICMS por substituição tributária' },
  { nameBD: '40', descricao: '40 - Isenta' },
  { nameBD: '41', descricao: '41 - Não tributada' },
  { nameBD: '50', descricao: '50 - Suspensão' },
  { nameBD: '51', descricao: '51 - Diferimento' },
  { nameBD: '60', descricao: '60 - ICMS cobrado anteriormente por substituição tributária' },
  { nameBD: '70', descricao: '70 - Com redução de base de cálculo e cobrança do ICMS por substituição tributária' },
  { nameBD: '90', descricao: '90 - Outras' },
];
// CST específico de tributação monofásica de ICMS sobre combustíveis (Convênio
// ICMS 199/2022) — só faz sentido pra empresa Regime Normal (crt 3), por isso
// fica fora de OPCOES_CST e é somado condicionalmente (ver opcoesCst abaixo).
const OPCOES_CST_MONOFASICO_COMBUSTIVEL = [
  { nameBD: '61', descricao: '61 - Tributação monofásica sobre combustíveis cobrada anteriormente' },
];

// Tabela 4.3.3/4.3.4 do SPED — mesmos códigos e descrições pra PIS e COFINS.
const OPCOES_CST_PIS_COFINS = [
  { nameBD: '01', descricao: '01 - Operação Tributável com Alíquota Básica' },
  { nameBD: '02', descricao: '02 - Operação Tributável com Alíquota Diferenciada' },
  { nameBD: '03', descricao: '03 - Operação Tributável com Alíquota por Unidade de Medida de Produto' },
  { nameBD: '04', descricao: '04 - Operação Tributável Monofásica - Revenda a Alíquota Zero' },
  { nameBD: '05', descricao: '05 - Operação Tributável por Substituição Tributária' },
  { nameBD: '06', descricao: '06 - Operação Tributável a Alíquota Zero' },
  { nameBD: '07', descricao: '07 - Operação Isenta da Contribuição' },
  { nameBD: '08', descricao: '08 - Operação sem Incidência da Contribuição' },
  { nameBD: '09', descricao: '09 - Operação com Suspensão da Contribuição' },
  { nameBD: '49', descricao: '49 - Outras Operações de Saída' },
  { nameBD: '50', descricao: '50 - Operação com Direito a Crédito - Vinculada Exclusivamente a Receita Tributada no Mercado Interno' },
  { nameBD: '51', descricao: '51 - Operação com Direito a Crédito – Vinculada Exclusivamente a Receita Não Tributada no Mercado Interno' },
  { nameBD: '52', descricao: '52 - Operação com Direito a Crédito - Vinculada Exclusivamente a Receita de Exportação' },
  { nameBD: '53', descricao: '53 - Operação com Direito a Crédito - Vinculada a Receitas Tributadas e Não-Tributadas no Mercado Interno' },
  { nameBD: '54', descricao: '54 - Operação com Direito a Crédito - Vinculada a Receitas Tributadas no Mercado Interno e de Exportação' },
  { nameBD: '55', descricao: '55 - Operação com Direito a Crédito - Vinculada a Receitas Não-Tributadas no Mercado Interno e de Exportação' },
  { nameBD: '56', descricao: '56 - Operação com Direito a Crédito - Vinculada a Receitas Tributadas e Não-Tributadas no Mercado Interno, e de Exportação' },
  { nameBD: '60', descricao: '60 - Crédito Presumido - Operação de Aquisição Vinculada Exclusivamente a Receita Tributada no Mercado Interno' },
  { nameBD: '61', descricao: '61 - Crédito Presumido - Operação de Aquisição Vinculada Exclusivamente a Receita Não-Tributada no Mercado Interno' },
  { nameBD: '62', descricao: '62 - Crédito Presumido - Operação de Aquisição Vinculada Exclusivamente a Receita de Exportação' },
  { nameBD: '63', descricao: '63 - Crédito Presumido - Operação de Aquisição Vinculada a Receitas Tributadas e Não-Tributadas no Mercado Interno' },
  { nameBD: '64', descricao: '64 - Crédito Presumido - Operação de Aquisição Vinculada a Receitas Tributadas no Mercado Interno e de Exportação' },
  { nameBD: '65', descricao: '65 - Crédito Presumido - Operação de Aquisição Vinculada a Receitas Não-Tributadas no Mercado Interno e de Exportação' },
  { nameBD: '66', descricao: '66 - Crédito Presumido - Operação de Aquisição Vinculada a Receitas Tributadas e Não-Tributadas no Mercado Interno, e de Exportação' },
  { nameBD: '67', descricao: '67 - Crédito Presumido - Outras Operações' },
  { nameBD: '70', descricao: '70 - Operação de Aquisição sem Direito a Crédito' },
  { nameBD: '71', descricao: '71 - Operação de Aquisição com Isenção' },
  { nameBD: '72', descricao: '72 - Operação de Aquisição com Suspensão' },
  { nameBD: '73', descricao: '73 - Operação de Aquisição a Alíquota Zero' },
  { nameBD: '74', descricao: '74 - Operação de Aquisição sem Incidência da Contribuição' },
  { nameBD: '75', descricao: '75 - Operação de Aquisição por Substituição Tributária' },
  { nameBD: '98', descricao: '98 - Outras Operações de Entrada' },
  { nameBD: '99', descricao: '99 - Outras Operações' },
];
// Tabela do CST IPI — vazio é uma opção válida pra produto sem incidência de IPI.
const OPCOES_CST_IPI = [
  { nameBD: '', descricao: 'Vazio' },
  { nameBD: '00', descricao: '00 - Entrada com recuperação de crédito' },
  { nameBD: '01', descricao: '01 - Entrada tributada com alíquota zero' },
  { nameBD: '02', descricao: '02 - Entrada isenta' },
  { nameBD: '03', descricao: '03 - Entrada não-tributada' },
  { nameBD: '04', descricao: '04 - Entrada imune' },
  { nameBD: '05', descricao: '05 - Entrada com suspensão' },
  { nameBD: '49', descricao: '49 - Outras entradas' },
  { nameBD: '50', descricao: '50 - Saída tributada' },
  { nameBD: '51', descricao: '51 - Saída tributada com alíquota zero' },
  { nameBD: '52', descricao: '52 - Saída isenta' },
  { nameBD: '53', descricao: '53 - Saída não-tributada' },
  { nameBD: '54', descricao: '54 - Saída imune' },
  { nameBD: '55', descricao: '55 - Saída com suspensão' },
  { nameBD: '99', descricao: '99 - Outras saídas' },
];

const formValidationSchema: yup.ObjectSchema<Omit<IFormData, 'iditens' | 'descricao' | 'ncm' | 'ean' | 'origem'>> = yup.object().shape({
  bcicmsst: yup.number().min(0, 'bcicmsst não pode ser negativo').required(),
  cfop: yup.number().required('CFOP é obrigatório'),
  cst: yup.string().required('CST é obrigatório'), 
  idprodutos: yup.string().required('Produto é obrigatório'),
  cstcofins: yup.string().optional().default(''),
  cstipi: yup.string().optional().default(''),
  cstpis: yup.string().optional().default(''),
  desconto: yup.number().min(0).required(),
  indtot: yup.number().oneOf([0, 1], 'indtot deve ser 0 ou 1').required(),
  modbc: yup.string().optional().default(''),
  modbcst: yup.string().optional().default(''),
  pcofins: yup.number().min(0).required(),
  picms: yup.number().min(0).required(),
  picmsst: yup.number().min(0).required(),
  pipi: yup.number().min(0).required(),
  ppis: yup.number().min(0).required(),
  preco: yup.number().moreThan(0, 'Preço deve ser maior que zero').required(),
  precotrib: yup.number().moreThan(0, 'Preço tributável deve ser maior que zero').required(),
  predbc: yup.number().min(0).required(),
  qtd: yup.number().moreThan(0, 'Quantidade deve ser maior que zero').required(),
  qtdtrib: yup.number().moreThan(0, 'Quantidade tributável deve ser maior que zero').required(),
  total: yup.number().moreThan(0, 'Valor total deve ser maior que zero').required(),
  un: yup.string().required('Unidade é obrigatória'),
  untrib: yup.string().required('Unidade tributável é obrigatória'),
  vbc: yup.number().min(0).required(),
  vbccofins: yup.number().min(0).required(),
  vbcipi: yup.number().min(0).required(),
  vbcpis: yup.number().min(0).required(),
  vcofins: yup.number().min(0).required(),
  vfrete: yup.number().min(0).required(),
  vicms: yup.number().min(0).required(),
  vicmsst: yup.number().min(0).required(),
  vipi: yup.number().min(0).required(),
  voutros: yup.number().min(0).required(),
  vpis: yup.number().min(0).required(),
  vseg: yup.number().min(0).required(),
  filtro: yup.string().optional().default(''),
  qtdglp: yup.number().optional(),
});

export const DialoNfe: React.FC<ConfirmationDialogRawProps> = (props: ConfirmationDialogRawProps) => {
  const { formRef, save } = useVForm();
  const { onClose, value: valueProp, open, problemasParaCorrigir, ...other } = props;
  const [value, setValue] = useState(valueProp);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingicms, setIsLoadingicms] = useState(true);
  const [id, setId] = useState('');
  const [filtro, setfiltro] = useState('descricao');
  const [nomeprod, setNomeProd] = useState('');
  const [produtoid, setProdutoid] = useState('');
  const [unprod, setUnProd] = useState('');
  const [ncmprod, setNcmProd] = useState('');
  const [referencia, setreferencia] = useState<string | undefined>('');
  const [marca, setmarca] = useState<string | undefined>('');
  const [un, setun] = useState<string | undefined>('');
  const [origem, setOrigem] = useState<number >(0);
  const [ean, setEan] = useState<string >('');
  // Quantidade de GLP derivado de petróleo do produto selecionado — quando
  // > 0, o Preço Tributado precisa de 10 casas decimais em vez das 4 padrão
  // (ver VFieldMoneyNumber4dn mais abaixo).
  const [qtdglp, setQtdglp] = useState(0);
  // Cst atual do item — só pra decidir se Percentual/P. Redução ICMS ficam
  // desabilitados (ver CST_SEM_REDUCAO_ICMS abaixo). Precisa de estado
  // próprio porque o valor mora no form do unform, que não re-renderiza os
  // outros campos quando muda.
  const [cstSelecionado, setCstSelecionado] = useState('');
  // Ind. Total atual do item — usado só pra mostrar o aviso de "não soma no
  // total" (o valor real de verdade mora no form do unform, que não
  // re-renderiza o Alert quando muda).
  const [indTotAtual, setIndTotAtual] = useState<number>(1);
  // Marca se o Cst que acabou de vir do produto selecionado é '00' — usado
  // por handlepicms (disparado logo depois, no mesmo onChange do
  // AutoCompleteProdutoIdProduto) pra saber se deve aplicar o percentual de
  // ICMS do produto. Precisa ser ref (não state) porque os dois callbacks
  // disparam em sequência síncrona: um state setter aqui não estaria
  // refletido ainda quando handlepicms roda.
  const cstZeroRef = useRef(false);
  // Percentual PIS/COFINS do produto selecionado — guardado à parte porque o
  // campo do form é zerado quando o CST PIS/COFINS sai de 01/02 (ver
  // handlecstpis/handlecstcofins); se o operador voltar pra 01/02 depois,
  // usa isso pra preencher de novo, sem precisar reselecionar o produto.
  const ppisProdutoRef = useRef(0);
  const pcofinsProdutoRef = useRef(0);
  // CST PIS/COFINS "atual" — atualizado de forma síncrona (ref, não state)
  // por handlecstpis/handlecstcofins, que disparam ANTES de handleppis/
  // handlepcofins no mesmo onChange de troca de produto (ver
  // AutoCompleteProdutoIdProduto.tsx). handleppis/handlepcofins consultam
  // isso pra decidir se aplicam o percentual bruto do produto ou zeram —
  // garante que o resultado final não dependa de qual handler escreve por
  // último (state do form via unform é batched/assíncrono; ref não é).
  const cstPisAtualRef = useRef<string | undefined>(undefined);
  const cstCofinsAtualRef = useRef<string | undefined>(undefined);
  // Mesma ideia pro Percentual ICMS do produto — usado por handlecst/
  // handlecsts pra restaurar o valor se o Cst voltar a ser '00' depois de
  // ter passado por outro (ver handlepicms mais abaixo).
  const picmsProdutoRef = useRef(0);
  // Trava contra disparo duplo de handleSave — o form do @unform aceita
  // submit tanto pelo clique no botão "Confirmar" (save()/submitForm())
  // quanto por Enter em qualquer campo (submit nativo do <form>), e os dois
  // caminhos chamam handleSave. Sem essa trava, um Enter seguido de clique
  // (ou o inverso) inseria o mesmo item duas vezes, porque a validação yup é
  // assíncrona e o `isLoading` só refletia no botão depois do primeiro
  // disparo já ter terminado. É ref (não state) pra bloquear de forma
  // síncrona, antes de qualquer re-render.
  const isSubmittingRef = useRef(false);

  const auth = getAuth();
  // 1 = Simples Nacional (CSOSN) — qualquer outro valor (3 = Regime Normal)
  // usa CST (mesmo critério de DetalheDeProduto.tsx).
  const crt = auth?.crt ?? 1;
  // Só pra empresa Regime Normal (crt 3): CST 30/40/41 (isenta/não tributada)
  // não tem base de cálculo reduzida de ICMS, então Percentual e P. Redução
  // ICMS ficam bloqueados nesses CSTs — qualquer outro CST libera os campos
  // normalmente, e empresas de outro crt nunca bloqueiam por essa regra.
  const CST_SEM_REDUCAO_ICMS = ['30', '40', '41'];
  const desabilitarReducaoIcms = crt === 3 && CST_SEM_REDUCAO_ICMS.includes(cstSelecionado);
  // CST 61 (monofásico combustíveis) só aparece pra empresa Regime Normal.
  const opcoesCst = crt === 3 ? [...OPCOES_CST, ...OPCOES_CST_MONOFASICO_COMBUSTIVEL] : OPCOES_CST;

  // Memoizado por sessão de abertura do dialog (não a cada render) — gerar
  // um novo a cada render mascarava um handleSave duplicado: as duas
  // chamadas geravam iditens diferentes e ambas caíam no "senão novo item"
  // de handleItens (DetalheDaNfe.tsx), inserindo duas linhas.
  const guuid = useMemo(() => uuidv4(), [open, props.id]);

  const itensnovo: IFormData = {
    iditens: '',
    idprodutos: '',
    modbcst: '',
    vfrete: 0,
    voutros: 0,
    predbc: 0,
    modbc: '',
    vicmsst: 0,
    ean: '',
    descricao: '',
    ncm: '',
    origem: 0,
    cfop: null,
    picmsst: 0,
    un: '',
    untrib: '',
    qtd: 0,
    qtdtrib: 0,
    preco: 0,
    precotrib: 0,
    cst: '400',
    vbc: 0,
    picms: 0,
    vicms: 0,
    bcicmsst: 0,
    desconto: 0,
    vseg: 0,
    cstpis: '08',
    ppis: 0,
    vipi: 0,
    cstipi: '',
    vbcipi: 0,
    pipi: 0,
    vpis: 0,
    vbcpis: 0,
    cstcofins: '08',
    vbccofins: 0,
    pcofins: 0,
    vcofins: 0,
    total: 0,
    indtot: 1,
    filtro: 'descricao'

  };

  useEffect(() => {
    if (props.id === 'novo') {
      formRef.current?.setData({
        ...itensnovo,
        pdesconto: 0,
        filtro: 'descricao'

      });
      setfiltro('descricao');
      setCstSelecionado(itensnovo.cst);
      setIndTotAtual(itensnovo.indtot);
      cstZeroRef.current = false;
      picmsProdutoRef.current = 0;
      ppisProdutoRef.current = 0;
      pcofinsProdutoRef.current = 0;
      cstPisAtualRef.current = itensnovo.cstpis;
      cstCofinsAtualRef.current = itensnovo.cstcofins;
      setQtdglp(0);
    } else {
      if (props.dados && typeof props.dados === 'object') {
        setNomeProd(props.dados.descricao);
        setNcmProd(props.dados.ncm);
        setUnProd(props.dados.un);
        // % do desconto salvo, calculado sobre o Valor Total do item.
        const totalItem = Number(props.dados.total) || 0;
        const descontoItem = Number(props.dados.desconto) || 0;
        formRef.current?.setData({
          ...props.dados,
          pdesconto: totalItem > 0 ? Number(((descontoItem / totalItem) * 100).toFixed(2)) : 0,
          filtro: 'descricao'
        });
        setfiltro('descricao');
        setCstSelecionado(props.dados.cst);
        setIndTotAtual(props.dados.indtot ?? 1);
        // Editando um item existente o Cst já é conhecido (veio salvo) — não
        // tem loading real acontecendo, então não faz sentido começar com
        // Percentual/P. Redução ICMS desabilitados (mesmo bug do handlecsts).
        setIsLoadingicms(false);
        // Percentuais já salvos no item viram a referência pra restaurar se o
        // operador trocar de Cst e voltar (ver handlecst/handlecstpis/
        // handlecstcofins) — sem isso, só uma nova seleção de produto
        // atualizaria essa referência.
        cstZeroRef.current = props.dados.cst === '00' || props.dados.cst === '0';
        picmsProdutoRef.current = Number(props.dados.picms) || 0;
        ppisProdutoRef.current = Number(props.dados.ppis) || 0;
        pcofinsProdutoRef.current = Number(props.dados.pcofins) || 0;
        cstPisAtualRef.current = props.dados.cstpis;
        cstCofinsAtualRef.current = props.dados.cstcofins;
        setQtdglp(Number(props.dados.qtdglp) || 0);
      }
    }
  }, [id, formRef, open]);
  useEffect(() => {
    if (open){
      setfiltro('descricao');
      // Só limpa nome/NCM/unidade do produto quando é um item NOVO — esse
      // efeito e o de cima (linha ~325, populando pelos dados salvos ao
      // editar) rodam no MESMO commit quando `open` vira true (os dois
      // dependem de `open`), na ordem em que estão declarados. Sem essa
      // guarda, ao ABRIR UM ITEM JÁ SALVO PRA EDIÇÃO, esse efeito roda
      // DEPOIS do de cima e zerava de volta nome/NCM/unidade que o de cima
      // acabava de preencher com props.dados — o item era salvo de novo sem
      // NCM (só "voltava a aparecer certo" depois de recarregar a nota,
      // porque o reload busca o NCM direto do cadastro do produto via join,
      // não do que ficou no state deste dialog).
      if (props.id === 'novo') {
        setNomeProd('');
        setNcmProd('');
        setUnProd('');
      }
      formRef.current?.setFieldValue('filtro', 'descricao');
    }
  }, [open, formRef, props.id]);
  const handleSave = (dados: IFormData) => {
    // Bloqueia reentrância síncrona — Enter em um campo (submit nativo do
    // <form>) seguido de clique no botão "Confirmar" (ou vice-versa) chamam
    // os dois o mesmo handleSave; sem essa checagem no topo, o segundo
    // disparo passava porque a validação abaixo é assíncrona.
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    formValidationSchema
      .validate(dados, { abortEarly: false })
      .then((validados) => {
        // pdesconto é só o auxílio de % da tela — o item grava o valor (desconto).
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { pdesconto, ...dadosValidados } = validados as typeof validados & { pdesconto?: number };
        setIsLoading(true);
        if (props.id === 'novo') {
          props.onDados({
            iditens: guuid,
            descricao: nomeprod,
            ncm: ncmprod,
            ean: ean,
            origem: origem,
            ...dadosValidados
          });
        } else {
          props.onDados({
            iditens: props.id,
            descricao: nomeprod,
            ncm: ncmprod,
            ean: ean,
            origem: origem,
            ...dadosValidados
          });
        }
        onClose(value);
        setIsLoading(false);
        isSubmittingRef.current = false;
      })
      .catch((errors: yup.ValidationError) => {
        isSubmittingRef.current = false;
        const validationErrors: IVFormErrors = {};
        errors.inner.forEach(error => {
          if (!error.path) return;
          validationErrors[error.path] = error.message;
        });
        formRef.current?.setErrors(validationErrors);
      });
  };
  const handleCancel = () => {
    onClose();
  };
  useEffect(() => {
    if (!open) {
      setValue(valueProp);
    } else {
      setId(valueProp);
    }
  }, [valueProp, open]);
  const handleProd = (descricao: string | undefined) => {
    if (descricao != null) {
      setNomeProd(descricao);
    }
  };
  const handleidproduto = (idproduto: string | undefined) => {
    setProdutoid(idproduto ?? '');
    if (idproduto) {
      formRef.current?.setFieldValue('qtd', 1);
      formRef.current?.setFieldValue('qtdtrib', 1);
    } else {
      formRef.current?.setData({
        ...itensnovo,
        filtro,
      });
      setNomeProd('');
      setNcmProd('');
      setUnProd('');
      setEan('');
      setmarca('');
      setreferencia('');
      setOrigem(0);
      setCstSelecionado(itensnovo.cst);
      setIndTotAtual(itensnovo.indtot);
      // Limpa produto = sem mais referência de percentual pra restaurar
      // depois (ver handlecst/handlecstpis/handlecstcofins).
      cstZeroRef.current = false;
      picmsProdutoRef.current = 0;
      ppisProdutoRef.current = 0;
      pcofinsProdutoRef.current = 0;
      cstPisAtualRef.current = itensnovo.cstpis;
      cstCofinsAtualRef.current = itensnovo.cstcofins;
      setQtdglp(0);
    }
  };
  const handleqtdglp = (valor: number | undefined) => {
    setQtdglp(valor ?? 0);
  };
  const handleun = (un: string | undefined) => {
    if (un != null) {
      setUnProd(un);
      formRef.current?.setFieldValue('un', un);
      formRef.current?.setFieldValue('untrib', un);
    }
  };
   const handleean = (ean: string | undefined) => {
    if (ean != null) {
      setEan(ean);
    }
  };
   const handleorigem = (origem: number | undefined) => {
    if (origem != null) {
      setOrigem(origem);
    }
  };
  // Produto com "TAX" desmarcado no cadastro (ver DetalheDeProduto.tsx) já
  // entra com Ind. Total desmarcado por padrão — o operador ainda pode
  // marcar de volta manualmente se quiser.
  const handletax = (tax: boolean | undefined) => {
    const novoIndTot = tax === false ? 0 : 1;
    formRef.current?.setFieldValue('indtot', novoIndTot);
    setIndTotAtual(novoIndTot);
  };
  const handleIndTot = (novoValor: number) => {
    setIndTotAtual(novoValor);
  };
  const handlepreco = (preco: number | undefined) => {
    if (preco != null) {
      formRef.current?.setFieldValue('preco', preco);
      formRef.current?.setFieldValue('precotrib', preco);
      const data = formRef.current?.getData();
      const quantidade = Number(data?.qtd) || 1;
      formRef.current?.setFieldValue('total', preco * quantidade);
    }
  };
  const handlecfop = (cfop: number | undefined) => {
    if (cfop != null) {
      formRef.current?.setFieldValue('cfop', cfop);
    } else {
      formRef.current?.setFieldValue('cfop', null);
    }
  };
  // Base de Cálculo de ICMS/PIS/COFINS é sempre qtdtrib × precotrib (ver
  // handlepicms/handlecstpis/handlecstcofins) — reaproveitada aqui pra
  // recalcular tudo de novo quando Quantidade, Preço Unitário ou Preço
  // Tributado mudam (o que muda um desses sempre acaba mudando qtdtrib
  // e/ou precotrib). Recebe os dois valores prontos por parâmetro em vez de
  // ler do form: cst/cstpis/cstcofins são campos que NÃO mudam nesses
  // handlers, então ler via getData() pra eles é seguro, mas qtdtrib/
  // precotrib podem ter acabado de ser setados no mesmo disparo síncrono —
  // getData() ainda leria o valor antigo (mesmo problema de sempre nos
  // VFieldMoneyNumber* deste projeto).
  const recalcularBaseTributos = (qtdtrib: number, precotrib: number) => {
    const base = qtdtrib * precotrib;
    const data = formRef.current?.getData();
    const cst = String(data?.cst ?? '');
    if (cst === '00' || cst === '0') {
      const picms = picmsProdutoRef.current;
      formRef.current?.setFieldValue('vbc', base);
      formRef.current?.setFieldValue('vicms', (picms * base) / 100);
    }
    if (data?.cstpis === '01' || data?.cstpis === '02') {
      const ppis = ppisProdutoRef.current;
      formRef.current?.setFieldValue('vbcpis', base);
      formRef.current?.setFieldValue('vpis', (ppis * base) / 100);
    }
    if (data?.cstcofins === '01' || data?.cstcofins === '02') {
      const pcofins = pcofinsProdutoRef.current;
      formRef.current?.setFieldValue('vbccofins', base);
      formRef.current?.setFieldValue('vcofins', (pcofins * base) / 100);
    }
  };
  const handlecalcularQtd = (valor: number | undefined) => {
    if (valor !== undefined && valor !== null) {
      const data = formRef.current?.getData();
      const preco = Number(data?.preco) || 0;
      const precotrib = Number(data?.precotrib) || 0;
      const tvalor = valor * preco;
      formRef.current?.setFieldValue('qtdtrib', valor);
      formRef.current?.setFieldValue('total', tvalor);
      recalcularBaseTributos(valor, precotrib);
      recalcularDescontoPeloPercentual(tvalor);
    }
  };
  const handlecalcularQtdTrib = (valor: number | undefined) => {
    if (valor === undefined || valor === null) return;
    const data = formRef.current?.getData();
    const precotrib = Number(data?.precotrib) || 0;
    recalcularBaseTributos(valor, precotrib);
  };
  const handlecalcularPrecoTrib = (valor: number | undefined) => {
    if (valor === undefined || valor === null) return;
    const data = formRef.current?.getData();
    const qtdtrib = Number(data?.qtdtrib) || 0;
    recalcularBaseTributos(qtdtrib, valor);
  };
  const handlebcimcs = (valor: number | undefined) => {
    if (valor !== undefined && valor !== null) {
      const data = formRef.current?.getData();
      const percentual = Number(data?.picms) || 0;
      const tvalor = (valor * percentual) / 100;
      formRef.current?.setFieldValue('vicms', tvalor);
    }
  };
  const handlepercentualicms = (valor: number | undefined) => {
    if (valor !== undefined && valor !== null) {
      // Operador editou o percentual na mão — vira a nova referência, senão
      // um Cst pra fora e de volta pra 00 (ver handlecst) restauraria o valor
      // antigo do produto, desfazendo a edição.
      picmsProdutoRef.current = valor;
      const data = formRef.current?.getData();
      const bcicms = Number(data?.vbc) || 0;
      const tvalor = (valor * bcicms) / 100;
      formRef.current?.setFieldValue('vicms', tvalor);
    }
  };
  const handlebcpis = (valor: number | undefined) => {
    if (valor === undefined || valor === null) return;
    const data = formRef.current?.getData();
    const ppis = Number(data?.ppis) || 0;
    formRef.current?.setFieldValue('vpis', (ppis * valor) / 100);
  };
  const handlepercentualpis = (valor: number | undefined) => {
    if (valor === undefined || valor === null) return;
    // Mesmo motivo do handlepercentualicms: edição manual vira a referência.
    ppisProdutoRef.current = valor;
    const data = formRef.current?.getData();
    const vbcpis = Number(data?.vbcpis) || 0;
    formRef.current?.setFieldValue('vpis', (valor * vbcpis) / 100);
  };
  const handlebccofins = (valor: number | undefined) => {
    if (valor === undefined || valor === null) return;
    const data = formRef.current?.getData();
    const pcofins = Number(data?.pcofins) || 0;
    formRef.current?.setFieldValue('vcofins', (pcofins * valor) / 100);
  };
  const handlepercentualcofins = (valor: number | undefined) => {
    if (valor === undefined || valor === null) return;
    pcofinsProdutoRef.current = valor;
    const data = formRef.current?.getData();
    const vbccofins = Number(data?.vbccofins) || 0;
    formRef.current?.setFieldValue('vcofins', (valor * vbccofins) / 100);
  };
  const handlecalcularPreco = (valor: number | undefined) => {
    if (valor !== undefined && valor !== null) {
      const data = formRef.current?.getData();
      const quantidade = data?.qtd || 0;
      const qtdtrib = Number(data?.qtdtrib) || 0;
      const tvalor = valor * quantidade;
      formRef.current?.setFieldValue('precotrib', valor);
      formRef.current?.setFieldValue('total', tvalor);
      recalcularBaseTributos(qtdtrib, valor);
      recalcularDescontoPeloPercentual(tvalor);
    }
  };
  // Desconto do item em % ou em valor, como Percentual PIS / Valor PIS: um
  // calcula o outro sobre o Valor Total do item. `pdesconto` só existe na
  // tela — não vai pro servidor (ver handleSave), o que é gravado é `desconto`.
  const handlepercentualdesconto = (valor: number | undefined) => {
    if (valor === undefined || valor === null) return;
    const total = Number(formRef.current?.getData()?.total) || 0;
    formRef.current?.setFieldValue('desconto', Number(((total * valor) / 100).toFixed(2)));
  };
  const handlevalordesconto = (valor: number | undefined) => {
    if (valor === undefined || valor === null) return;
    const total = Number(formRef.current?.getData()?.total) || 0;
    formRef.current?.setFieldValue('pdesconto', total > 0 ? Number(((valor / total) * 100).toFixed(2)) : 0);
  };
  // Quantidade/Preço mudaram o Valor Total: com um % informado, o valor do
  // desconto acompanha (mesmo comportamento do Valor PIS com a base).
  // Recebe o total novo por parâmetro — getData() ainda leria o antigo.
  const recalcularDescontoPeloPercentual = (total: number) => {
    const pdesconto = Number(formRef.current?.getData()?.pdesconto) || 0;
    if (pdesconto > 0) formRef.current?.setFieldValue('desconto', Number(((total * pdesconto) / 100).toFixed(2)));
  };
  const handlecst = (valor: string | undefined) => {
    if (valor != null) {
      setCstSelecionado(valor);
      // Toda troca de Cst invalida a Base de Cálculo calculada pro Cst
      // anterior — zera aqui; se o novo Cst for '00', o valor certo é
      // recalculado logo abaixo.
      formRef.current?.setFieldValue('vbc', 0);
      // Cst 60 (ICMS cobrado antes por substituição tributária) não tem Mod
      // BC — não existe base de cálculo pra escolher o modo.
      if (valor === '60') {
        formRef.current?.setFieldValue('modbc', '');
      }
      if (valor === '00' || valor === '0') {
        // Restaura o percentual do último produto conhecido (picmsProdutoRef)
        // — "preenche de novo" ao voltar pra 00 depois de ter passado por
        // outro Cst, mesma lógica de handlecstpis/handlecstcofins.
        setTimeout(() => {
          const data = formRef.current?.getData();
          const qtdtrib = Number(data?.qtdtrib) || 0;
          const precotrib = Number(data?.precotrib) || 0;
          const vbc = qtdtrib * precotrib;
          const picms = picmsProdutoRef.current;
          formRef.current?.setFieldValue('picms', picms);
          formRef.current?.setFieldValue('vbc', vbc);
          formRef.current?.setFieldValue('vicms', (picms * vbc) / 100);
        }, 0);
      } else {
        formRef.current?.setFieldValue('picms', 0);
        formRef.current?.setFieldValue('vicms', 0);
      }
      if (valor === '3' || valor === '4' || valor === '5') {
        formRef.current?.setFieldValue('vicms', 0);
        formRef.current?.setFieldValue('picms', 0);
        formRef.current?.setFieldValue('vbcicms', 0);
        formRef.current?.setFieldValue('cst', valor);
        setIsLoadingicms(true);
      } else {
        if (isLoading) {
          setIsLoadingicms(true);
        } else {
          setIsLoadingicms(false);
        }
      }
    }
  };
  const handleFiltro = (valor: string | undefined) => {
    if (valor != null) {
      setfiltro(valor);
      formRef.current?.setFieldValue('filtro', valor);
    }
  };
  const handlemarca = (valor: string | undefined) => {
    if (valor !== undefined) {
      setmarca(valor);
    }
  };
  const handlecstcofins = (valor: string | undefined) => {
    if (valor !== undefined) {
      formRef.current?.setFieldValue('cstcofins', valor);
      // Síncrono (ref, não state) — precisa estar atualizado ANTES de
      // handlepcofins rodar (mesmo onChange de troca de produto, dispara
      // depois deste — ver AutoCompleteProdutoIdProduto.tsx), pra ele saber
      // se deve aplicar o percentual bruto do produto ou zerar. Garante o
      // resultado certo sem depender de qual handler escreve por último.
      cstCofinsAtualRef.current = valor;
      if (valor === '01' || valor === '02') {
        // Preenche de novo o percentual do produto de forma síncrona — cobre
        // tanto uma troca de produto (handlepcofins, que dispara depois no
        // mesmo onChange, escreve o mesmo valor de novo) quanto o operador
        // mudando o CST COFINS manualmente de volta pra 01/02 sem trocar de
        // produto (handlepcofins não dispara nesse caso).
        formRef.current?.setFieldValue('pcofins', pcofinsProdutoRef.current);
        // setTimeout: qtdtrib/precotrib só refletem o setFieldValue no próximo
        // render (VFieldMoneyNumber2) — precisa esperar esse próximo tick pra
        // calcular Base/Valor certos.
        setTimeout(() => {
          const data = formRef.current?.getData();
          const qtdtrib = Number(data?.qtdtrib) || 0;
          const precotrib = Number(data?.precotrib) || 0;
          const vbccofins = qtdtrib * precotrib;
          const pcofins = pcofinsProdutoRef.current;
          formRef.current?.setFieldValue('vbccofins', vbccofins);
          formRef.current?.setFieldValue('vcofins', (pcofins * vbccofins) / 100);
        }, 0);
      } else {
        // Zera tudo, inclusive o percentual — precisa ser síncrono e direto
        // aqui (não só via handlepcofins) porque uma troca MANUAL do CST
        // COFINS (sem trocar de produto) nunca dispara handlepcofins.
        formRef.current?.setFieldValue('vbccofins', 0);
        formRef.current?.setFieldValue('vcofins', 0);
        formRef.current?.setFieldValue('pcofins', 0);
      }
    }
  };
  const handlecstipi = (valor: string | undefined) => {
    if (valor !== undefined) {
      formRef.current?.setFieldValue('cstipi', valor);
    }
  };
  const handlecstpis = (valor: string | undefined) => {
    if (valor !== undefined) {
      formRef.current?.setFieldValue('cstpis', valor);
      // Síncrono (ref, não state) — precisa estar atualizado ANTES de
      // handleppis rodar (mesmo onChange de troca de produto, dispara depois
      // deste — ver AutoCompleteProdutoIdProduto.tsx), pra ele saber se deve
      // aplicar o percentual bruto do produto ou zerar. Garante o resultado
      // certo sem depender de qual handler escreve por último.
      cstPisAtualRef.current = valor;
      if (valor === '01' || valor === '02') {
        // Preenche de novo o percentual do produto de forma síncrona — cobre
        // tanto uma troca de produto (handleppis, que dispara depois no
        // mesmo onChange, escreve o mesmo valor de novo) quanto o operador
        // mudando o CST PIS manualmente de volta pra 01/02 sem trocar de
        // produto (handleppis não dispara nesse caso).
        formRef.current?.setFieldValue('ppis', ppisProdutoRef.current);
        // setTimeout: qtdtrib/precotrib só refletem o setFieldValue no próximo
        // render (VFieldMoneyNumber2) — precisa esperar esse próximo tick pra
        // calcular Base/Valor certos.
        setTimeout(() => {
          const data = formRef.current?.getData();
          const qtdtrib = Number(data?.qtdtrib) || 0;
          const precotrib = Number(data?.precotrib) || 0;
          const vbcpis = qtdtrib * precotrib;
          const ppis = ppisProdutoRef.current;
          formRef.current?.setFieldValue('vbcpis', vbcpis);
          formRef.current?.setFieldValue('vpis', (ppis * vbcpis) / 100);
        }, 0);
      } else {
        // Zera tudo, inclusive o percentual — precisa ser síncrono e direto
        // aqui (não só via handleppis) porque uma troca MANUAL do CST PIS
        // (sem trocar de produto) nunca dispara handleppis.
        formRef.current?.setFieldValue('vbcpis', 0);
        formRef.current?.setFieldValue('vpis', 0);
        formRef.current?.setFieldValue('ppis', 0);
      }
    }
  };
  const handlepipi = (valor: number | undefined) => {
    if (valor !== undefined) {
      formRef.current?.setFieldValue('pipi', valor);
    }
  };
  const handleppis = (valor: number | undefined) => {
    if (valor !== undefined) {
      // Guarda sempre o percentual bruto do produto — é o que handlecstpis
      // usa pra "preencher de novo" se o operador voltar pro Cst 01/02
      // depois de ter trocado pra outro.
      ppisProdutoRef.current = valor;
      // Só ESCREVE no campo se o Cst PIS atual (cstPisAtualRef, atualizado
      // por handlecstpis — que dispara ANTES deste no mesmo onChange de
      // troca de produto) realmente usa percentual. Sem essa checagem, numa
      // troca de produto pra um Cst diferente de 01/02, este handler
      // reescrevia o percentual bruto por cima do zero que handlecstpis
      // já tinha aplicado — Base/Valor ficavam certos (zerados), mas o
      // campo Percentual PIS mostrava um número que não deveria.
      const cstAtual = cstPisAtualRef.current;
      const aplicaPercentual = cstAtual === '01' || cstAtual === '02';
      formRef.current?.setFieldValue('ppis', aplicaPercentual ? valor : 0);
    }
  };
  const handlepcofins = (valor: number | undefined) => {
    if (valor !== undefined) {
      // Ver comentário equivalente em handleppis.
      pcofinsProdutoRef.current = valor;
      const cstAtual = cstCofinsAtualRef.current;
      const aplicaPercentual = cstAtual === '01' || cstAtual === '02';
      formRef.current?.setFieldValue('pcofins', aplicaPercentual ? valor : 0);
    }
  };
  const handlencm = (valor: string | undefined) => {
    if (valor !== undefined) {
      setNcmProd(valor);
    }
  };
   const handlecsts = (valor: string | undefined) => {
    if (valor !== undefined) {
      formRef.current?.setFieldValue('cst', valor);
      setCstSelecionado(valor);
      // Toda troca de Cst invalida a Base de Cálculo do Cst anterior — zera
      // aqui; se este novo Cst for '00', handlepicms recalcula em seguida.
      formRef.current?.setFieldValue('vbc', 0);
      // Cst 60 (ICMS cobrado antes por substituição tributária) não tem Mod
      // BC — não existe base de cálculo pra escolher o modo. Mesma regra de
      // handlecst, aqui pro caso de vir junto com a troca de produto.
      if (valor === '60') {
        formRef.current?.setFieldValue('modbc', '');
      }
      // isLoadingicms só virava false quando o operador mexia manualmente no
      // select de Cst (ver handlecst) — ao trocar de produto, o Cst vem
      // preenchido automaticamente por aqui e isLoadingicms ficava travado no
      // `true` inicial, mantendo Percentual/P. Redução ICMS desabilitados
      // mesmo com um Cst que não deveria bloquear (ex.: 00).
      setIsLoadingicms(false);
      // handlepicms (mesmo onChange do AutoCompleteProdutoIdProduto) dispara
      // logo depois — só aplica o percentual/BC de ICMS do produto se o Cst
      // que acabou de vir junto for '00'.
      cstZeroRef.current = valor === '00' || valor === '0';
    }
  };
  const handlepicms = (valor: number | undefined) => {
    if (valor === undefined) return;
    // Guarda sempre, independente do Cst atual — é o que handlecst usa pra
    // "preencher de novo" o Percentual se o operador voltar pro Cst 00
    // depois de ter trocado pra outro.
    picmsProdutoRef.current = valor;
    if (cstZeroRef.current) {
      formRef.current?.setFieldValue('picms', valor);
      // setTimeout: mesmo motivo do handlecstpis — qtdtrib/precotrib acabaram
      // de ser setados por outros handlers deste mesmo disparo síncrono.
      setTimeout(() => {
        const data = formRef.current?.getData();
        const qtdtrib = Number(data?.qtdtrib) || 0;
        const precotrib = Number(data?.precotrib) || 0;
        const vbc = qtdtrib * precotrib;
        formRef.current?.setFieldValue('vbc', vbc);
        formRef.current?.setFieldValue('vicms', (valor * vbc) / 100);
      }, 0);
    } else {
      formRef.current?.setFieldValue('picms', 0);
      formRef.current?.setFieldValue('vicms', 0);
    }
  };
  const handlereferencia = (valor: string | undefined) => {
    setreferencia(valor);
  };
const handleUnChange = (valor: string | undefined) => {
  if (valor !== undefined && valor !== null) {
    formRef.current?.setFieldValue('untrib', valor);
    setUnProd(valor);
  }
};
  const renderProductTitle = () => {
    const productName = nomeprod || props.dados?.descricao || 'Novo Produto';
    const isEditing = props.id !== 'novo';
    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 48,
            height: 48,
            borderRadius: '12px',
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
            color: 'primary.dark',
            border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            flexShrink: 0,
            mt: 0.5,
          }}
        >
          {isEditing ? <EditIcon /> : <AddIcon />}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
            {isEditing ? 'Editar Item da Nota Fiscal' : 'Novo Item da Nota Fiscal'}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography
              variant="body1"
              sx={{
                color: 'text.primary',
                fontWeight: 500,
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                whiteSpace: 'normal',
                lineHeight: 1.4,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {productName}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {isEditing && (
                <Chip
                  label="Editando"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{
                    height: 22,
                    fontSize: '0.7rem',
                    borderWidth: '1px',
                    alignSelf: 'flex-start',
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };
  return (
    <Dialog
      open={open}
      {...other}
      sx={{ width: '100%' }}
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
          maxWidth: '900px',
        }
      }}
    >
      <Box
        sx={{
          background: (theme) => theme.palette.background.paper,
          color: 'text.primary',
          position: 'relative',
          pt: 3,
          pb: 2,
          px: 3,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <IconButton
          aria-label="close"
          onClick={handleCancel}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            color: 'text.secondary',
            backgroundColor: 'action.hover',
            '&:hover': {
              backgroundColor: 'action.selected',
              color: 'text.primary',
            }
          }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        {renderProductTitle()}
        {!!problemasParaCorrigir?.length && (
          <Alert
            severity={problemasParaCorrigir.some((problema) => problema.severidade === 'erro') ? 'error' : 'warning'}
            variant="outlined"
            sx={{ mt: 2, whiteSpace: 'pre-line' }}
          >
            {problemasParaCorrigir.map((problema) => `${problema.campo}: ${problema.mensagem}`).join('\n')}
          </Alert>
        )}
        <Divider
          sx={{
            mt: 2,
            mb: 1.5,
          }}
        />

        <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap' }}>
          {props.dados?.un && (
            <Chip
              icon={<InventoryIcon sx={{ fontSize: 14 }} />}
              label={`UN: ${unprod}`}
              size="small"
              variant="outlined"
              sx={{
                height: 24,
                fontSize: '0.75rem',
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.3),
                color: 'text.secondary',
                mb: 0.5,
              }}
            />
          )}
          {props.dados?.ncm && (
            <Chip
              label={`NCM: ${ncmprod}`}
              size="small"
              variant="outlined"
              sx={{
                height: 24,
                fontSize: '0.75rem',
                borderColor: (theme) => alpha(theme.palette.secondary.main, 0.3),
                color: 'text.secondary',
                mb: 0.5,
              }}
            />
          )}
          
        </Stack>
      </Box>
      <DialogContent dividers sx={{ p: 3 }}>
        <VForm ref={formRef} onSubmit={handleSave}placeholder={undefined} onPointerEnterCapture={undefined}onPointerLeaveCapture={undefined}>
          <Grid container direction="column">
            <Grid container item direction="row" padding={1} spacing={1}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Grid container item direction="row" spacing={2} >
                  <Grid item xs={12} sm={12} md={10} lg={8} xl={8}>
                    <AutoCompleteProdutoIdProduto
                      filtro={filtro}
                      isExternalLoading={props.status === 1 ? isLoading : true}
                      descricao={handleProd}
                      marca={handlemarca}
                      idproduto={handleidproduto}
                      referencia={handlereferencia}
                      un={handleun}
                      Cfop={handlecfop}
                      origem={handleorigem}
                      ean={handleean}
                      pVenda={handlepreco}
                      cst={handlecsts}
                      cstcofins={handlecstcofins}
                      cstipi={handlecstipi}
                      cstpis={handlecstpis}
                      picms={handlepicms}
                      pipi={handlepipi}
                      ppis={handleppis}
                      pcofins={handlepcofins}
                      ncm={handlencm}
                      qtdglp={handleqtdglp}
                      tax={handletax}
                    />
                  </Grid>
                  <Grid item xs={4} sm={4} md={2} lg={2} xl={2}>
                    <BasicSelect
                      name='filtro'
                      label='Filtro'
                      aoClicar={handleFiltro}
                      data={[
                        { nameBD: 'descricao', descricao: 'Descrição' },
                        { nameBD: 'codbarraentrada', descricao: 'Código de Barras Entrada' },
                        { nameBD: 'referencia', descricao: 'Referência' },
                      ]}
                      disabled={props.status === 1 ? isLoading : true}
                    />
                  </Grid>
                  <Grid item xs={4} sm={4} md={2} lg={2} xl={2}>
                    <VTextField
                      fullWidth
                      size='small'
                      name='un'
                      label='Unidade'
                      disabled={props.status === 1 ? isLoading : true}
                      onChange={(e) => handleUnChange(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={4} sm={4} md={2} lg={2} xl={2}>
                    <VTextField
                      fullWidth
                      size='small'
                      name='untrib'
                      label='Unidade Tributada'
                      disabled={props.status === 1 ? isLoading : true}
                    />
                  </Grid>
                  <Grid item xs={4} sm={4} md={2} lg={2} xl={2}>
                    <VSwitchNumber
                      name='indtot'
                      label='Ind. Total'
                      fdisabled={props.status === 1 ? isLoading : true}
                      aliamento='top'
                      valor={handleIndTot}
                    />
                  </Grid>
                  {indTotAtual === 0 && (
                    <Grid item xs={12}>
                      <Alert severity="warning" variant="outlined" sx={{ py: 0 }}>
                        O valor deste item não será somado ao total da nota.
                      </Alert>
                    </Grid>
                  )}

                  <Grid item xs={8} sm={8} md={6} lg={6} xl={6}>
                    <AutoCompleteCfop 
                    isExternalLoading={props.status === 1 ? isLoading : true }
                     />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VFieldMoneyNumber2
                      fullWidth
                      size='small'
                      name='qtd'
                      label='Quantidade'
                      disabled={props.status === 1 ? isLoading : true}
                      calcular={handlecalcularQtd}
                      
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VFieldMoneyNumber2
                      fullWidth
                      size='small'
                      name='qtdtrib'
                      label='Quantidade Tributada'
                      disabled={props.status === 1 ? isLoading : true}
                      calcular={handlecalcularQtdTrib}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VFieldMoneyNumber4dn
                      fullWidth
                      size='small'
                      name='preco'
                      label='Preço Unitario'
                      disabled={props.status === 1 ? isLoading : true}
                      calcular={handlecalcularPreco}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VFieldMoneyNumber4dn
                      fullWidth
                      size='small'
                      name='precotrib'
                      label='Preço Tributado'
                      disabled={props.status === 1 ? isLoading : true}
                      calcular={handlecalcularPrecoTrib}
                      // Produto com GLP derivado de petróleo (qtdglp > 0)
                      // precisa de 10 casas decimais no Preço Tributado —
                      // ver VFieldMoneyNumber4dn. Com 10 casas, digitar a
                      // vírgula manualmente evita ter que preencher todas
                      // as casas decimais só pra chegar na parte inteira.
                      decimais={qtdglp > 0 ? 10 : 4}
                      digitarVirgula={qtdglp > 0}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                   <VFieldMoneyNumber4dn
                      fullWidth
                      size='small'
                      name='total'
                      label='Valor Total'
                      disabled={true}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <BasicSelect
                      name='cst'
                      label='Cst'
                      aoClicar={handlecst}
                      data={crt === 1 ? OPCOES_CSOSN : opcoesCst}
                      disabled={props.status === 1 ? isLoading : true}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <BasicSelect
                      name='modbc'
                      label='Mod BC'
                      data={[
                        { nameBD: '', descricao: '' },
                        { nameBD: '0', descricao: '0 - Margem Valor Agregado (%)' },
                        { nameBD: '1', descricao: '1 - Pauta (Valor)' },
                        { nameBD: '2', descricao: '2 - Preço Tabelado Máx. (valor)' },
                        { nameBD: '3', descricao: '3 - Valor da operação' },
                      ]}
                      disabled={props.status === 1 ? isLoading : true}
                    />
                  </Grid>
                   <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VFieldMoneyNumber2
                      fullWidth
                      size='small'
                      name='vbc'
                      label='Base De Calculo'
                      calcular={handlebcimcs}
                      disabled={props.status === 1 ? isLoading : true}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                   <VFieldMoneyNumber4dn
                      fullWidth
                      size='small'
                      name='picms'
                      label='Percentual'
                      calcular={handlepercentualicms}
                      disabled={isLoadingicms || desabilitarReducaoIcms}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VFieldMoneyNumber4dn
                      fullWidth
                      size='small'
                      name='predbc'
                      label='P. Redução ICMS'
                      disabled={isLoadingicms || desabilitarReducaoIcms}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VFieldMoneyNumber4dn
                      fullWidth
                      size='small'
                      name='vicms'
                      label='Valor ICMS'
                      disabled={true}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <BasicSelect
                      name='cstipi'
                      label='CST IPI'
                      data={OPCOES_CST_IPI}
                      disabled={props.status === 1 ? isLoading : true}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VFieldMoneyNumber2
                      fullWidth
                      size='small'
                      name='vbcipi'
                      label='B.C IPI'
                      disabled={props.status === 1 ? isLoading : true}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VFieldMoneyNumber2
                      fullWidth
                      size='small'
                      name='pipi'
                      label='Percentual IPI'
                      disabled={props.status === 1 ? isLoading : true}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VFieldMoneyNumber2
                      fullWidth
                      size='small'
                      name='vipi'
                      label='Valor IPI'
                      disabled={props.status === 1 ? isLoading : true}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VFieldMoneyNumber2
                      fullWidth
                      size='small'
                      name='vfrete'
                      label='Frete'
                      disabled={props.status === 1 ? isLoading : true}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VFieldMoneyNumber2
                      fullWidth
                      size='small'
                      name='pdesconto'
                      label='Percentual Desconto'
                      disabled={props.status === 1 ? isLoading : true}
                      calcular={handlepercentualdesconto}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VFieldMoneyNumber2
                      fullWidth
                      size='small'
                      name='desconto'
                      label='Valor Desconto'
                      disabled={props.status === 1 ? isLoading : true}
                      calcular={handlevalordesconto}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <BasicSelect
                      name='cstpis'
                      label='CST PIS'
                      aoClicar={handlecstpis}
                      data={OPCOES_CST_PIS_COFINS}
                      disabled={props.status === 1 ? isLoading : true}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VFieldMoneyNumber2
                      fullWidth
                      size='small'
                      name='vbcpis'
                      label='B.C PIS'
                      disabled={props.status === 1 ? isLoading : true}
                      calcular={handlebcpis}
                    />
                  </Grid>

                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VFieldMoneyNumber2
                      fullWidth
                      size='small'
                      name='ppis'
                      label='Percentual PIS'
                      disabled={props.status === 1 ? isLoading : true}
                      calcular={handlepercentualpis}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VFieldMoneyNumber2
                      fullWidth
                      size='small'
                      name='vpis'
                      label='Valor PIS'
                      disabled={props.status === 1 ? isLoading : true}
                    />
                  </Grid>
                 
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VFieldMoneyNumber2
                      fullWidth
                      size='small'
                      name='voutros'
                      label='Outros Valor'
                      disabled={props.status === 1 ? isLoading : true}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={2} lg={2} xl={2}>
                    <VFieldMoneyNumber2
                      fullWidth
                      size='small'
                      name='vseg'
                      label='Seguro'
                      disabled={props.status === 1 ? isLoading : true}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <BasicSelect
                      name='cstcofins'
                      label='CST COFINS'
                      aoClicar={handlecstcofins}
                      data={OPCOES_CST_PIS_COFINS}
                      disabled={props.status === 1 ? isLoading : true}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VFieldMoneyNumber2
                      fullWidth
                      size='small'
                      name='vbccofins'
                      label='B.C CONFIS'
                      disabled={props.status === 1 ? isLoading : true}
                      calcular={handlebccofins}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VFieldMoneyNumber2
                      fullWidth
                      size='small'
                      name='pcofins'
                      label='Percentual CONFIS'
                      disabled={props.status === 1 ? isLoading : true}
                      calcular={handlepercentualcofins}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VFieldMoneyNumber2
                      fullWidth
                      size='small'
                      name='vcofins'
                      label='Valor CONFIS'
                      disabled={props.status === 1 ? isLoading : true}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <BasicSelect
                      name='modbcst'
                      label='Mod BC ST'
                      data={[
                        { nameBD: '0', descricao: '0 - Preço tabelado ou máximo sugerido' },
                        { nameBD: '1', descricao: '1 - Lista Negativa (valor)' },
                        { nameBD: '2', descricao: '2 - Lista Positiva (valor)' },
                        { nameBD: '3', descricao: '3 - Lista Neutra (valor)' },
                        { nameBD: '4', descricao: '4 - Margem Valor Agregado (%)' },
                        { nameBD: '5', descricao: '5 - Pauta (valor)' },
                        { nameBD: '6', descricao: '6 - Valor da Operação' },
                      ]}
                      disabled={props.status === 1 ? isLoading : true}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VFieldMoneyNumber2
                      fullWidth
                      size='small'
                      name='bcicmsst'
                      label='B.C ICMS ST'
                      disabled={props.status === 1 ? isLoading : true}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VFieldMoneyNumber2
                      fullWidth
                      size='small'
                      name='picmsst'
                      label='Percentual ST'
                      disabled={props.status === 1 ? isLoading : true}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VFieldMoneyNumber2
                      fullWidth
                      size='small'
                      name='vicmsst'
                      label='Valor ICMS ST'
                      disabled={props.status === 1 ? isLoading : true}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </VForm>
      </DialogContent>
      <DialogActions sx={{ 
        p: 2.5, 
        pt: 2,
        backgroundColor: 'background.default',
        borderTop: 1,
        borderColor: 'divider'
      }}>
        <Stack 
          direction="row" 
          spacing={1.5} 
          sx={{ 
            width: '100%',
            justifyContent: 'flex-end'
          }}
        >
          <Button
            variant="outlined"
            onClick={handleCancel}
            startIcon={<CancelIcon />}
            sx={{
              px: 2.5,
              py: 0.75,
              borderRadius: '8px',
              borderColor: 'grey.300',
              color: 'grey.700',
              fontWeight: 500,
              textTransform: 'none',
              fontSize: '0.875rem',
              '&:hover': {
                borderColor: 'error.main',
                backgroundColor: (theme) => alpha(theme.palette.error.main, 0.04),
                color: 'error.dark',
              }
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={props.status === 1 ? isLoading : true}
            onClick={save}
            startIcon={<CheckCircleIcon />}
            sx={{
              px: 2.5,
              py: 0.75,
              borderRadius: '8px',
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              fontWeight: 500,
              textTransform: 'none',
              fontSize: '0.875rem',
              '&:hover': {
                backgroundColor: brand.primaryHover,
                boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
              },
              '&:disabled': {
                backgroundColor: 'action.disabledBackground',
                color: 'action.disabled',
                boxShadow: 'none',
              }
            }}
          >
            Confirmar
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};